import { usePageView } from '@fs/zion-analytics'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Cell, Grid, Row, H5 } from '@fs/zion-ui'
import { Link, useParams } from '@fs/zion-router'
import { PlaceCemetary } from '@fs/zion-icon/dist/cjs/icons'
import { usePageTitle } from '../../app/PageTitleContext'
import DataView from '../../common/DataView'
import { utcToLocaleString } from '../../../util/DateUtil'
import { CisLink, GroupLink, PersonLink } from '../../app/Links'
import { getBooleanDisplayValue, getIdElement } from '../../../util/StringUtil'
import { saveTree } from '../../../services/treeService'
import useNotifications from '../../../hooks/useNotifications'
import { getFrontendBaseUrl } from '../../../util/UrlUtil'
import PersonsTable from './PersonsTable'
import { TreeContext } from '../TreeContextProvider'
import EditableTreeField from './EditableTreeField'

export default function TreeSummary() {
  usePageView({ page_detail: 'DMConsole:TreeSummary' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tree.summary'))
  const { error } = useNotifications()
  const { state, actions } = useContext(TreeContext)
  const [isEditing, setIsEditing] = useState(false)
  const { treeId } = useParams()
  const editableFields = useMemo(
    () => ['name', 'tombstoned', 'private', 'ordinancesAllowed', 'useSecondaryAdminQueues'],
    []
  )
  const noShowFields = ['collectionId', 'hidden']
  const editableFieldsRef = useRef({})

  useEffect(() => {
    editableFieldsRef.current = editableFields.reduce((acc, field) => {
      acc[field] = state.tree?.[field]
      return acc
    }, {})
  }, [editableFields, state.tree])

  const getValue = (key, value) => {
    let theValue = value
    if (key.toLowerCase().includes('date')) {
      theValue = utcToLocaleString(value, true)
    } else if (key === 'startingPersonId') {
      theValue = <PersonLink personId={value} showId={false} newTab />
    } else if (key === 'ownerCisId') {
      theValue = <CisLink userId={getIdElement(value)} showId={false} newTab />
    } else if (key === 'groups') {
      theValue = state.tree?.groups?.groups
        ? Object.entries(state.tree.groups.groups).map(([groupKey, group]) => {
            return (
              <div key={groupKey}>
                <GroupLink groupId={group.id} groupName={group.name} showId newTab />
                {group.tombstoned && <PlaceCemetary />}
              </div>
            )
          })
        : []
    } else if (
      key === 'hidden' ||
      key === 'private' ||
      key === 'tombstoned' ||
      key === 'researcherTree' ||
      key === 'autoPublicAccepted' ||
      key === 'ordinancesAllowed' ||
      key === 'useSecondaryAdminQueues'
    ) {
      theValue = getBooleanDisplayValue(value)
    } else if (key === 'ownerAccess' || key === 'groupAccess') {
      theValue = t(`tree.thirdpartyAccess.${value}`, value)
    }

    return theValue
  }

  const treeInfoData = () => {
    // don't show all fields
    if (state.tree) {
      noShowFields.forEach((fieldName) => {
        delete state.tree[fieldName]
      })
    }

    return state.tree
      ? Object.entries(state.tree).map(([key, value]) => {
          return {
            key: `tree-info-${key}`,
            label: t(`tree.info.${key}`, key),
            detail: editableFields.includes(key) ? (
              <EditableTreeField
                fieldKey={key}
                value={value}
                isEditing={isEditing}
                getValue={getValue}
                editableFieldsRef={editableFieldsRef}
              />
            ) : (
              getValue(key, value)
            ),
          }
        })
      : []
  }

  const save = () => {
    saveTree(treeId, { ...state.tree, ...editableFieldsRef.current })
      .then(() => {
        actions.loadTree()
      })
      .catch((err) => {
        actions.setTree(state.originalTree)
        error(
          t('tree.error.save', `Error saving tree: {message}`, {
            message: err.response?.data?.notifications?.[0]?.msg || err.message,
          })
        )
      })
  }

  return (
    <>
      {state.tree && (
        <Grid>
          <Cell columns={12}>
            <H5>
              <Row wrap alignX="start">
                <h1 style={{ marginTop: '4px', fontSize: '24pt' }}>{`${
                  state.originalTree?.name || state.tree.name || t('tree.label', 'Tree')
                }`}</h1>

                <Link to={`${getFrontendBaseUrl(true)}/tree/pedigree/landscape/?treeId=${treeId}`} target="_blank">
                  <Button emphasis="medium">{t('tree.viewInFamilySearch', 'View in FamilySearch')}</Button>
                </Link>
                {!isEditing && (
                  <Button
                    emphasis="low"
                    onClick={() => {
                      setIsEditing(true)
                    }}
                  >
                    {t('action.edit', 'Edit')}
                  </Button>
                )}
                {isEditing && (
                  <Row>
                    <Button
                      emphasis="high"
                      onClick={() => {
                        save()
                        setIsEditing(false)
                      }}
                    >
                      {t('action.save', 'Save')}
                    </Button>
                    <Button
                      emphasis="medium"
                      onClick={() => {
                        setIsEditing(false)
                      }}
                    >
                      {t('action.cancel', 'Cancel')}
                    </Button>
                  </Row>
                )}
              </Row>
            </H5>
          </Cell>

          <Cell columns={12}>{state.tree && <DataView id="tree-info" data={treeInfoData()} />}</Cell>

          <Cell columns={12}>
            <PersonsTable treeId={treeId} />
          </Cell>
        </Grid>
      )}
    </>
  )
}
